import { CalendarClock, ListChecks, Pin } from 'lucide-react'
import { Interactive, Popover } from '@highlight/ui'
import { CellPreviewCard, HOVER_CARD_TRIGGER, usePinnedDisclosure } from '../cell-preview-card'
import { ReminderDatePicker } from './date-picker'
import { ReminderSource } from './reminder-source'
import { formatDatePill } from './utils/due-date'
import { isPinnedReminder } from './utils/pinned'
import { usePreferredHourCycle } from './utils/user-time-format'
import { useReminderActions, type ReminderItem } from '../../hooks/use-reminders'
import { feedbackSubject } from '@renderer/chat/api/feedback'

interface ReminderPreviewProps {
  item: ReminderItem
  onSnooze: (date: Date | null) => void
  /** Fired after a pin mutation so hosts can flash their saving state. */
  onMutated?: () => void
}

/**
 * Reminder variant of the cell hover card (HIG-5838).
 *
 * The card describes the reminder and carries its due date and its pin.
 * Checking off, editing and deleting live on the cell's hover button cluster.
 */
export function ReminderPreview({ item, onSnooze, onMutated }: ReminderPreviewProps) {
  const [, setFeedbackOpen] = usePinnedDisclosure()
  const feedback = feedbackSubject(item)

  // `ReminderSource` renders nothing without an origin, so the row is only
  // worth a slot — and only worth connecting to the title — when there is one.
  const hasSource = !!item.source

  return (
    <CellPreviewCard.Shell
      icon={<ListChecks size={14} />}
      title={item.title}
      // The title is the reminder's whole body — keep its line breaks.
      multilineTitle
      sourceConnected={hasSource}
      source={hasSource ? <ReminderSource item={item} wrap /> : undefined}
      feedback={
        feedback ? (
          <CellPreviewCard.FeedbackPill {...feedback} source="reminder" onOpenChange={setFeedbackOpen} />
        ) : undefined
      }
      actions={<ReminderPills item={item} onSnooze={onSnooze} onMutated={onMutated} trigger={HOVER_CARD_TRIGGER} />}
    />
  )
}

interface ReminderPillsProps extends ReminderPreviewProps {
  /** Which surface the pin was toggled from, for Amplitude. */
  trigger: string
}

/** The due date and pin as pills, shared by the hover card and the To-dos popover. */
export function ReminderPills({ item, onSnooze, onMutated, trigger }: ReminderPillsProps) {
  const [snoozeOpen, setSnoozeOpen] = usePinnedDisclosure()
  const hourCycle = usePreferredHourCycle()
  const { setPinned } = useReminderActions()
  const pinned = isPinnedReminder(item)

  const handleTogglePin = (): void => {
    setPinned(item.id, !pinned, { trigger })
    onMutated?.()
  }

  return (
    <>
      <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
        <Popover.Trigger
          render={
            // Dashed while unset — an invitation to fill the value in; once
            // a date exists the pill goes solid and wears it as its label.
            <CellPreviewCard.PillButton outline={item.scheduledAt ? 'solid' : 'dashed'} />
          }
        >
          <CalendarClock />
          {item.scheduledAt ? formatDatePill(item.scheduledAt, hourCycle) : 'Set due date'}
        </Popover.Trigger>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          initialFocus={false}
          positionerClassName="z-[80]"
          className="w-[320px] p-1"
        >
          <Interactive>
            <ReminderDatePicker value={item.scheduledAt} onChange={onSnooze} />
          </Interactive>
        </Popover.Content>
      </Popover>
      <CellPreviewCard.PillButton
        outline={pinned ? 'solid' : 'dashed'}
        selected={pinned}
        aria-label={pinned ? 'Unpin reminder' : 'Pin reminder'}
        onClick={handleTogglePin}
      >
        <Pin className={pinned ? 'fill-current' : undefined} />
        {pinned ? 'Pinned' : 'Pin'}
      </CellPreviewCard.PillButton>
    </>
  )
}
